import { useCallback } from 'react';
import { useTranscriber } from './useTranscriber';
import { useRealtimeConnection } from './useRealtimeConnection';

/**
 * Hook to manage audio recording modes (Whisper transcription or realtime)
 * @param {Object} config - Configuration object
 * @param {'transcribe'|'realtime'} config.mode - Recording mode
 * @param {Function} config.toggleMode - Callback to toggle between 'transcribe' and 'realtime' modes
 * @param {Object} config.transcribeConfig - Configuration for Whisper transcription
 * @param {string} config.transcribeConfig.language - Language code for transcription ('es', 'en', 'pt')
 * @param {Function} config.transcribeConfig.onTranscriptionComplete - Callback when transcription completes
 * @param {Function} config.transcribeConfig.onTranscriptionStart - Callback when transcription starts
 * @param {Function} config.transcribeConfig.onTranscriptionEnd - Callback when transcription ends
 * @returns {Object} Audio recorder state and control functions
 * @returns {boolean} returns.isRecording - Whether currently recording
 * @returns {boolean} returns.isTranscribing - Whether transcribing (Whisper mode only)
 * @returns {Float32Array} returns.audioData - Audio frequency data for visualization
 * @returns {string|null} returns.errorCode - Error code if any
 * @returns {Function} returns.startRecording - Start recording audio
 * @returns {Function} returns.stopRecording - Stop recording audio
 * @returns {Function} returns.resetError - Reset error state
 * @returns {Object} returns.realtimeConfig - Realtime connection configuration and state
 */
export const useAudioRecorder = ({ mode = 'transcribe', toggleMode, transcribeConfig}) => {
  const isRealtimeMode = mode === 'realtime';

  const realtimeConfig = useRealtimeConnection( {toggleMode});

  // Use Whisper transcription hook
  const whisperHook = useTranscriber(
    isRealtimeMode ? {} : transcribeConfig || {}
  );

  // For realtime mode, delegate to the realtime functions
  const startRecording = useCallback(async () => {
    if (isRealtimeMode) {
      if (!realtimeConfig?.isConnected) {
        return;
      }
      await realtimeConfig.startRecording?.();
    } else {
      await whisperHook.startRecording();
    }
  }, [isRealtimeMode, realtimeConfig, whisperHook]);

  const stopRecording = useCallback(async () => {
    if (isRealtimeMode) {
      await realtimeConfig?.stopRecording?.();
    } else {
      await whisperHook.stopRecording();
    }
  }, [isRealtimeMode, realtimeConfig, whisperHook]);

  const cancelRecording = useCallback(async () => {
    if (isRealtimeMode) {
      await realtimeConfig?.cancelRecording?.();
    } else {
      await whisperHook.cancelRecording();
    }
  }, [isRealtimeMode, realtimeConfig, whisperHook]);

  // Return appropriate state based on mode
  const isRecording = isRealtimeMode
    ? Boolean(realtimeConfig?.isRecording)
    : whisperHook.isRecording;

  const audioData = isRealtimeMode
    ? realtimeConfig?.audioData || new Float32Array()
    : whisperHook.audioData;

  const isTranscribing = isRealtimeMode ? false : whisperHook.isTranscribing;

  const errorCode = isRealtimeMode
    ? (!realtimeConfig?.isConnected && isRecording ? 'realtimeUnavailable' : null)
    : whisperHook.errorCode;

  return {
    isRecording,
    isTranscribing,
    audioData,
    errorCode,
    startRecording,
    stopRecording,
    cancelRecording,
    resetError: whisperHook.resetError,
    realtimeConfig
  };
};

export default useAudioRecorder;
